<!-- eslint-disable vue/no-v-html -->
<template>
  <ClientOnly fallback-tag="span" fallback="Loading ...">
    <div class="mt-7 w-full xl:max-w-[650px]">
      <!-- overflow-hidden clips each tab's 1000%-wide base line at the strip's edge -->
      <div class="tabs relative z-[1] flex overflow-hidden" role="tablist">
        <div v-for="(item, index) in tabs" :key="index" class="mr-10 shrink-0">
          <div class="flex py-3 hover:text-blue">
            <button
              :id="`${uid}-tab-${index}`"
              type="button"
              role="tab"
              :aria-selected="index === activeTab"
              :aria-controls="`${uid}-panel-${index}`"
              :tabindex="index === activeTab ? 0 : -1"
              class="relative block cursor-pointer font-sans text-base font-bold uppercase before:absolute before:-bottom-3 before:left-0 before:h-0.5 before:w-[1000%] before:bg-[#CAF7FF] before:content-[''] after:absolute after:-bottom-3 after:left-0 after:z-10 after:h-1 after:w-full after:content-[''] focus-visible:outline-offset-[-2px] lg:text-base"
              :class="[index === activeTab ? 'text-blue after:bg-blue' : 'text-black']"
              @click="setActiveTab(index, item.value)"
              @keydown="onTabKeydown($event, index, i => setActiveTab(i, tabs[i]!.value))"
            >
              {{ item.title }}
            </button>
          </div>
        </div>
      </div>

      <TabPanels :active="activeTab" class="tabs-content mt-8">
        <div :id="`${uid}-panel-0`" role="tabpanel" :aria-labelledby="`${uid}-tab-0`">
          <div class="rounded-xl bg-blue-lighter px-6 lg:rounded-2xl lg:px-9">
            <h4 class="mb-6 font-serif text-2xl text-black lg:text-28">
              {{ $t('delivery.printed1.title') }}
            </h4>
            <p class="mb-6 font-sans text-lg text-black">{{ $t('delivery.printed1.text') }}</p>

            <p
              v-if="deliveryInfo.lateMessage && !deliveryInfo.afterLastDate"
              class="mb-4 text-blue"
            >
              {{ deliveryInfo.lateMessage }}
            </p>
            <!-- Help text if lastDate has been set in Contentful and the delivery date is after lastDate -->
            <p v-if="deliveryInfo.afterLastDate" class="mb-4 text-blue">
              <span v-if="props.lateMessage">
                {{
                  `Observera att vi inte längre kan garantera leverans till ${deliveryInfo.lateMessage}. Om du vill kan du istället välja `
                }}
                <button
                  class="underline"
                  @click="
                    () => {
                      setActiveTab(1, DELIVERY_TYPE.DIGITAL)
                      scrollToTop()
                    }
                  "
                >
                  digital leverans,
                </button>
                då skickas gåvokortet till din e-post direkt efter att du betalat.
              </span>
              <span v-else>
                Gåvobeviset kommer inte komma fram i tid, om du vill kan du istället välja
                <button
                  class="cursor-pointer underline"
                  @click="
                    () => {
                      setActiveTab(1, DELIVERY_TYPE.DIGITAL)
                      scrollToTop()
                    }
                  "
                >
                  digital leverans,
                </button>
                det skickas i så fall som pdf till din e-post direkt efter att du betalat.
              </span>
            </p>
            <!---->

            <Button
              :title="t('button.next')"
              color="pink"
              class="mb-4 w-full lg:mt-8 lg:w-[315px]"
              @click="() => handleClickNext(true)"
              :arrow-right="true"
            />
          </div>
          <div
            class="relative my-7 flex w-full items-center justify-center before:absolute before:block before:h-[1px] before:w-full before:bg-black before:content-[''] lg:mt-10"
          >
            <span class="relative z-10 bg-blue-lighter px-3 text-lg lg:text-[20px]">{{
              t('or')
            }}</span>
          </div>
          <div class="rounded-xl bg-blue-lighter px-6 py-4 lg:rounded-2xl lg:px-9">
            <h4 class="mb-6 font-serif text-2xl text-black lg:text-28">
              {{ $t('delivery.printed2.title') }}
            </h4>
            <p class="mb-6 font-sans text-lg text-black">{{ $t('delivery.printed2.text') }}</p>
            <div class="flex flex-col lg:flex-row lg:gap-4">
              <Input
                :label="t('delivery.printed2.firstname')"
                v-model="modelDelivery.name.first"
                style-input="border !border-blue-dark focus:border-blue-dark shadow-[0_12px_33px_0_rgba(5, 58, 65, 0.04)] w-full "
                class="mb-7"
                :v$="v$?.name.first"
                style-error="!relative"
                required
              />
              <Input
                :label="t('delivery.printed2.lastname')"
                v-model="modelDelivery.name.last"
                style-input="border !border-blue-dark focus:border-blue-dark shadow-[0_12px_33px_0_rgba(5, 58, 65, 0.04)] w-full "
                class="mb-7"
                :v$="v$?.name.last"
                style-error="!relative"
                required
              />
            </div>
            <div class="flex flex-col lg:flex-row lg:gap-4">
              <Input
                :label="t('delivery.printed2.street-address')"
                v-model="modelDelivery.address.street"
                style-input="border !border-blue-dark focus:border-blue-dark shadow-[0_12px_33px_0_rgba(5, 58, 65, 0.04)] w-full "
                class="mb-7 grow-[2]"
                :v$="v$?.address?.street"
                style-error="!relative"
                required
              />
              <Input
                :label="t('delivery.printed2.postcode')"
                v-model="modelDelivery.address.postcode"
                style-input="border !border-blue-dark focus:border-blue-dark shadow-[0_12px_33px_0_rgba(5, 58, 65, 0.04)] w-full "
                class="mb-7"
                :v$="v$?.address?.postcode"
                style-error="!relative"
                required
              />
            </div>
            <div>
              <RadioGroup
                v-model="modelDelivery.delivery.when"
                :options="dayOfDelivery"
                :label="$t('delivery.printed2.option.label')"
              />
              <DatePicker
                v-if="modelDelivery.delivery.when === 'day'"
                :min-date="dayjs(new Date()).format('YYYY-MM-DD')"
                v-model:date="modelDelivery.delivery.date"
                :label="$t('delivery.printed2.option.datepicker-label')"
                style-input="border !border-blue-dark focus:!border-blue-dark !shadow-[0_12px_33px_0_rgba(5, 58, 65, 0.04)] w-full "
                class="mb-7 w-1/2"
                :v$="v$?.delivery?.date"
                style-error="!relative"
                required
              />
              <p v-if="deliveredBy" class="mb-4 font-semibold">
                Beräknad leverans är mellan {{ deliveredBy }}.
              </p>

              <p class="mb-6 font-sans text-sm text-black">
                {{ $t('delivery.printed2.text-bottom') }}
              </p>

              <p v-if="deliveryInfo.lateMessage && !deliveryInfo.afterLastDate" class="text-blue">
                {{ deliveryInfo.lateMessage }}
              </p>
              <!-- Help text if lastDate has been set in Contentful and the delivery date is after lastDate -->
              <p v-if="deliveryInfo.afterLastDate" class="mb-4 text-blue">
                <span v-if="props.lateMessage">
                  {{
                    `Observera att vi inte längre kan garantera leverans till ${deliveryInfo.lateMessage}. Om du vill kan du istället välja `
                  }}
                  <button
                    class="underline"
                    @click="
                      () => {
                        setActiveTab(1, DELIVERY_TYPE.DIGITAL)
                        scrollToTop()
                      }
                    "
                  >
                    digital leverans,
                  </button>
                  då skickas gåvokortet till din e-post direkt efter att du betalat.
                </span>
                <span v-else>
                  Gåvobeviset kommer inte komma fram i tid, om du vill kan du istället välja
                  <button
                    class="cursor-pointer underline"
                    @click="
                      () => {
                        setActiveTab(1, DELIVERY_TYPE.DIGITAL)
                        scrollToTop()
                      }
                    "
                  >
                    digital leverans,
                  </button>
                  det skickas i så fall som pdf till din e-post direkt efter att du betalat.
                </span>
              </p>
              <!---->
            </div>

            <Button
              :title="t('button.next')"
              color="pink"
              class="mb-6 mt-7 w-full lg:mb-0 lg:mt-8 lg:w-[315px]"
              @click="() => handleClickNext(false)"
              :arrow-right="true"
            />
          </div>
        </div>
        <div :id="`${uid}-panel-1`" role="tabpanel" :aria-labelledby="`${uid}-tab-1`">
          <div class="lg:p-x rounded-xl bg-blue-lighter px-6 lg:rounded-2xl">
            <h4 class="mb-6 font-serif text-2xl text-black lg:text-28">
              {{ $t('delivery.digital.title') }}
            </h4>
            <p class="mb-6 font-sans text-lg text-black">{{ $t('delivery.digital.text-top') }}</p>
            <Input
              :label="t('delivery.digital.email')"
              v-model="modelDelivery.email"
              style-input="border !border-blue-dark focus:border-blue-dark shadow-[0_12px_33px_0_rgba(5, 58, 65, 0.04)] w-full "
              class="mb-7"
              :v$="v$?.email"
              style-error="!relative"
              required
            />
            <Button
              :title="t('button.next')"
              color="pink"
              class="mt-7 w-full lg:mt-8 lg:w-[315px]"
              @click="handleClickNext"
              :arrow-right="true"
            />
          </div>
        </div>
      </TabPanels>
    </div>
  </ClientOnly>
</template>

<script setup lang="ts">
import type { Validation } from '@vuelidate/core'
import { ref, nextTick, useId } from 'vue'
import { useVModel } from '@vueuse/core'
import { Input, Button, RadioGroup, TabPanels, onTabKeydown } from 'refresh-ui'
import dayjs from 'dayjs'
import 'dayjs/locale/sv'
import 'dayjs/locale/en'
import 'dayjs/locale/uk'
import 'dayjs/locale/es'

import { useI18n } from 'vue-i18n'
import { DELIVERY_TYPE } from '../../types/deliveryType'
import DatePicker from './DatePicker.vue'

export interface IGiftDeliveryValidation {
  email: string
  name: {
    first: string
    last: string
  }
  address: {
    street: string
    postcode: string
  }
  delivery?: {
    data?: string
  }
}

const { t, locale } = useI18n()

const props = defineProps<{
  delivery: any
  v$: Validation<IGiftDeliveryValidation>
  lastDate?: string
  lateMessage?: string
}>()

const tabs = ref([
  {
    title: t('delivery.printed'),
    value: DELIVERY_TYPE.PRINTED,
  },
  {
    title: t('delivery.digital'),
    value: DELIVERY_TYPE.DIGITAL,
  },
])

const dayOfDelivery = ref([
  {
    label: t('delivery.printed2.option.delivery-asap'),
    value: 'soon',
  },
  {
    label: t('delivery.printed2.option.delivery-date'),
    value: 'day',
  },
])

const emits = defineEmits(['update:delivery', 'onNext'])

const modelDelivery = useVModel(props, 'delivery', emits)

watch(
  () => modelDelivery.value.delivery.when,
  (newVal, oldVal) => {
    if (oldVal === 'day' && newVal === 'soon') {
      modelDelivery.value.delivery.date = ''
    }
  },
)

const uid = useId()
const activeTab = ref(0)
const scrollToTop = () => window.scrollTo({ top: 500, behavior: 'smooth' })

const handleClickNext = async (mailToYou: boolean) => {
  modelDelivery.value.mailToYou = mailToYou
  await nextTick()
  if (props.v$) {
    props.v$.$touch()
    props.v$.$validate()
    if (!props.v$.$invalid) emits('onNext')
    return
  }
  emits('onNext')
}

const setActiveTab = (index: number, value: string) => {
  activeTab.value = index
  modelDelivery.value.deliveryType = value
}

const deliveredBy = computed(() => {
  if (!modelDelivery.value.delivery?.date || !dayjs(modelDelivery.value.delivery?.date).isValid())
    return undefined
  const earliestDelivery = dayjs(modelDelivery.value.delivery.date)
    .add(3, 'day')
    .locale(locale.value)
    .format('D MMMM')
  const latestDelivery = dayjs(modelDelivery.value.delivery.date)
    .add(5, 'day')
    .locale(locale.value)
    .format('D MMMM')
  if (earliestDelivery.split(' ')[1] === latestDelivery.split(' ')[1]) {
    return `${earliestDelivery.split(' ')[0]}-${latestDelivery}`
  }
  return `${earliestDelivery} - ${latestDelivery}`
})

const deliveryInfo = computed(() => {
  const result = {
    afterLastDate: false,
    lateMessage: undefined as string | undefined,
    deliverBeforeLastDate: true,
    formattedDate: undefined as string | undefined,
  }
  if (!props.lastDate) return result
  result.formattedDate = dayjs(props.lastDate).locale(locale.value).format('D MMMM')
  // Check if today is after lastDate
  if (new Date() > new Date(props.lastDate)) {
    result.afterLastDate = true
    result.lateMessage = props.lateMessage
  } else {
    result.lateMessage = t('delivery.lateMessage2', {
      message: props.lateMessage,
      date: result.formattedDate,
    })
  }
  // Check if selected delivery date is before or same as lastDate
  if (
    modelDelivery.value.delivery?.date &&
    dayjs(modelDelivery.value.delivery?.date).isValid() &&
    props.lastDate
  ) {
    result.deliverBeforeLastDate =
      dayjs(modelDelivery.value.delivery.date).isBefore(dayjs(props.lastDate)) ||
      dayjs(modelDelivery.value.delivery.date).isSame(dayjs(props.lastDate))
  }
  return result
})
</script>

<style>
.tabs,
.tabs-content {
  padding-bottom: 0;
}
</style>
